import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { ScrollableContainer } from '../../../components/ui/ScrollableContainer';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setGuards, updateGuard, clearAttendance } from '../../../store/slices/attendanceSlice';
import toast from 'react-hot-toast';
import { FiEye, FiClock, FiChevronRight, FiRotateCw, FiSettings, FiEdit } from 'react-icons/fi';
import { FaChevronLeft } from 'react-icons/fa';
import {
    loadAllGuardsForExport,
    getGuardsPage,
    getGuardsTotalCount,
    GuardData,
} from '../../manage_guard/service/guardService';
import type { DocumentSnapshot } from '../../manage_guard/service/guardService';
import {
    calculateMonthlySummary,
    getCurrentDateInfo,
    getDaysInMonth,
    formatDate,
    getMonthlyAttendance,
    AttendanceSummary,
    AttendanceStatus,
} from '../service/attendanceService';
import AttendanceModal from './AttendanceModal';
import AttendanceHistoryModal from './AttendanceHistoryModal';
import SalaryDistributionModal from './SalaryDistributionModal';
import SalarySummaryModal from './SalarySummaryModal';
import SalaryCycleConfigModal from './SalaryCycleConfigModal';
import GuardSalaryDeploymentModal from './GuardSalaryDeploymentModal';

const SearchAndActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[3]};
  gap: ${theme.spacing[3]};
  width: 100%;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
  min-width: 250px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
  }
`;




const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background-color: ${theme.colors.light.background.secondary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing[2]};
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: 2px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.2;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  min-height: 0;
`;


const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.medium};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[100]};
  }

  &:hover {
    border-color: ${theme.colors.primary[400]};
  }
`;


const Badge = styled.span<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'success' }) => {
        if (variant === 'success') return `${theme.colors.accent.success}20`;
        if (variant === 'danger') return `${theme.colors.accent.danger}20`;
        if (variant === 'info') return `${theme.colors.accent.info}20`;
        return `${theme.colors.accent.warning}20`;
    }};
  color: ${({ variant = 'success' }) => {
        if (variant === 'success') return theme.colors.accent.success;
        if (variant === 'danger') return theme.colors.accent.danger;
        if (variant === 'info') return theme.colors.accent.info;
        return theme.colors.accent.warning;
    }};
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background-color: ${theme.colors.white};
  position: relative;
`;

const TableBodyWrapper = styled.div<{ showScrollbar?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  
  /* Scrollbar overlay only on body - auto-hide */
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary[400]} transparent;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Hide scrollbar buttons (arrows) */
  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.primary[500]} 100%);
    border-radius: 4px;
    opacity: ${({ showScrollbar }) => showScrollbar ? 0.8 : 0.3};
    transition: opacity 1s ease 1.5s;
  }
  
  &:hover::-webkit-scrollbar-thumb {
    opacity: 0.9;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
    background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  }
  
  /* Always show scrollbar when scrolling */
  &.scrolling::-webkit-scrollbar-thumb {
    opacity: 0.8;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
  background-color: ${theme.colors.white};
  
  @media (max-width: 1200px) {
    width: 1200px;
    min-width: 1200px;
  }
  
  thead {
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-bottom: 2px solid ${theme.colors.primary[200]};
    flex-shrink: 0;
    
    tr:first-child th:first-child {
      border-top-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:first-child th:last-child {
      border-top-right-radius: ${theme.borderRadius.lg};
    }
  }
  
  th {
    text-align: center;
    white-space: normal;
    line-height: 1.4;
    vertical-align: middle;
    font-weight: ${theme.typography.fontWeight.bold};
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.primary[700]};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    position: relative;
    
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: linear-gradient(180deg, transparent, ${theme.colors.primary[300]}, transparent);
    }
  }
  
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.light.text.primary};
    vertical-align: middle;
  }
  
  /* Center align all cells except first column (Guard Info) */
  td:not(:first-child) {
    text-align: center;
  }
  
  /* Keep Guard Info left-aligned */
  td:first-child {
    text-align: left;
  }
  
  /* Column widths */
  th:nth-child(1), td:nth-child(1) { width: 28%; } /* Guard Info */
  th:nth-child(2), td:nth-child(2) { width: 12%; } /* Deployment Date */
  th:nth-child(3), td:nth-child(3) { width: 6%; }  /* Present */
  th:nth-child(4), td:nth-child(4) { width: 6%; }  /* Absent */
  th:nth-child(5), td:nth-child(5) { width: 6%; }  /* Leave */
  th:nth-child(6), td:nth-child(6) { width: 10%; } /* Monthly Salary */
  th:nth-child(7), td:nth-child(7) { width: 10%; } /* Salary Till Today */
  th:nth-child(8), td:nth-child(8) { width: 10%; } /* Advance Salary */
  th:nth-child(9), td:nth-child(9) { width: 10%; } /* Balance */
  th:nth-child(10), td:nth-child(10) { width: 4%; } /* Actions */
  
  /* Row hover effect */
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${theme.colors.primary[50]} !important;
    }
    
    &:nth-child(even) {
      background-color: ${theme.colors.white};
    }
    
    &:nth-child(odd) {
      background-color: ${theme.colors.light.background.secondary};
    }
  }
  
  /* Footer styling - sticky with header matching background */
  tfoot {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-top: 2px solid ${theme.colors.primary[200]};
    
    tr:last-child td:first-child {
      border-bottom-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:last-child td:last-child {
      border-bottom-right-radius: ${theme.borderRadius.lg};
    }
    
    td {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      text-align: center;
      position: relative;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
  width: 100%;
  flex-wrap: wrap;
  padding: ${theme.spacing[1]} 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[1]};
  }
`;

const PaginationNavButton = styled.button<{ disabled?: boolean; isNext?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${({ disabled, isNext }) =>
        disabled
            ? theme.colors.light.background.tertiary
            : isNext
                ? theme.colors.primary[600]
                : theme.colors.light.background.tertiary};
  color: ${({ disabled, isNext }) =>
        disabled
            ? theme.colors.light.text.muted
            : isNext
                ? theme.colors.white
                : theme.colors.light.text.secondary};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${({ isNext }) =>
        isNext
            ? theme.colors.primary[700]
            : theme.colors.light.border.medium};
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageNumberButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[600]};
  background-color: ${({ active }) =>
        active
            ? theme.colors.white
            : theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.white};
    color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Ellipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  user-select: none;
`;

export interface GuardWithAttendance extends GuardData {
    attendance?: AttendanceSummary;
    todayStatus?: AttendanceStatus;
}

const AttendancePage: React.FC = () => {
    const { user, companyData } = useAuth();
    const dispatch = useAppDispatch();
    const { guards: reduxGuards, companyId: reduxCompanyId } = useAppSelector((state) => state.attendance);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGuard, setSelectedGuard] = useState<GuardData | null>(null);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyGuard, setHistoryGuard] = useState<GuardData | null>(null);
    const [showSalaryDistribution, setShowSalaryDistribution] = useState(false);
    const [showSalarySummary, setShowSalarySummary] = useState(false);
    const [showSalaryCycleConfig, setShowSalaryCycleConfig] = useState(false);
    const [showSalaryDeploymentModal, setShowSalaryDeploymentModal] = useState(false);
    const [salaryGuard, setSalaryGuard] = useState<GuardData | null>(null);
    const [selectedMonth, setSelectedMonth] = useState(getCurrentDateInfo().month);
    const [selectedYear, setSelectedYear] = useState(getCurrentDateInfo().year);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalGuardCount, setTotalGuardCount] = useState(0);
    const pageCursorsRef = useRef<(DocumentSnapshot | null)[]>([]);
    const [showScrollbar, setShowScrollbar] = useState(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const PAGE_SIZE_OPTIONS = [10, 15, 50, 100];

    const companyId = companyData?.id || user?.uid || '';
    const currentDate = getCurrentDateInfo();
    const todayDateKey = formatDate(currentDate.year, currentDate.month, currentDate.day);
    const isInitialLoad = useRef(true);

    // Current page of guards (from Firebase pagination) – no longer use Redux for list
    const [guards, setGuardsState] = useState<GuardWithAttendance[]>([]);

    useEffect(() => {
        if (!user || !companyId) return;
        getGuardsTotalCount(companyId).then(setTotalGuardCount).catch((e) => {
            console.error('Error loading total count:', e);
        });
    }, [user, companyId]);

    useEffect(() => {
        if (!user || !companyId) return;
        isInitialLoad.current = false;
        loadGuardsWithAttendance();
    }, [user, companyId, selectedMonth, selectedYear, currentPage, pageSize]);

    // Reset to page 1 when search term or page size changes; clear cursors when page size changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);
    useEffect(() => {
        setCurrentPage(1);
        pageCursorsRef.current = [];
    }, [pageSize]);

    const loadGuardsWithAttendance = async () => {
        setLoading(true);
        try {
            const cursor = currentPage === 1 ? undefined : pageCursorsRef.current[currentPage - 2] ?? undefined;
            const { guards: pageGuards, lastDoc } = await getGuardsPage(companyId, pageSize, cursor ?? null);

            const guardsWithAttendance = await Promise.all(
                pageGuards.map(async (guard) => {
                    try {
                        const attendance = await calculateMonthlySummary(
                            guard,
                            selectedYear,
                            selectedMonth,
                            companyId
                        );
                        const monthlyAttendance = await getMonthlyAttendance(
                            guard.id,
                            selectedYear,
                            selectedMonth,
                            companyId
                        );
                        const todayStatus = monthlyAttendance.days[todayDateKey];
                        const defaultStatus = guard.currentDeployment ? 'present' : 'undeployed';
                        return {
                            ...guard,
                            attendance,
                            todayStatus: (todayStatus || defaultStatus) as AttendanceStatus
                        };
                    } catch (error) {
                        console.error(`Error loading attendance for guard ${guard.id}:`, error);
                        return { ...guard, attendance: undefined, todayStatus: 'present' as AttendanceStatus };
                    }
                })
            );

            setGuardsState(guardsWithAttendance);
            dispatch(setGuards({ guards: guardsWithAttendance, companyId }));

            if (lastDoc && currentPage > pageCursorsRef.current.length) {
                pageCursorsRef.current = [...pageCursorsRef.current, lastDoc];
            }
        } catch (error: any) {
            console.error('Error loading guards:', error);
            toast.error(error.message || 'Failed to load guards');
            setGuardsState([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter guards: show guards who were active in selected month OR have outstanding balance
    const filteredGuards = useMemo(() => {
        const targetDate = new Date(selectedYear, selectedMonth - 1, 1);
        const nextMonthDate = new Date(selectedYear, selectedMonth, 1);

        const result = guards.filter((g) => {
            // 1. Check for deployment overlap with selected month
            let wasActiveInMonth = false;

            // Robust date parsing (handles Timestamps, ISO, and DD/MM/YYYY strings)
            const parseDate = (dateVal: any): Date | null => {
                if (!dateVal) return null;
                if (dateVal instanceof Date) return dateVal;
                if (typeof dateVal.toDate === 'function') return dateVal.toDate();
                if (dateVal.seconds) return new Date(dateVal.seconds * 1000);

                // Handle string formats
                if (typeof dateVal === 'string') {
                    // Check for DD/MM/YYYY format
                    const dmyMatch = dateVal.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                    if (dmyMatch) {
                        return new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
                    }
                    const d = new Date(dateVal);
                    return isNaN(d.getTime()) ? null : d;
                }
                return null;
            };

            // Check current deployment
            if (g.currentDeployment?.postDate) {
                const start = parseDate(g.currentDeployment.postDate);
                const end = parseDate(g.currentDeployment.endDate);
                if (start && start < nextMonthDate && (!end || end >= targetDate)) {
                    wasActiveInMonth = true;
                }
            }

            // Check current OT deployment
            if (!wasActiveInMonth && g.currentOTDeployment?.postDate) {
                const start = parseDate(g.currentOTDeployment.postDate);
                const end = parseDate(g.currentOTDeployment.endDate);
                if (start && start < nextMonthDate && (!end || end >= targetDate)) {
                    wasActiveInMonth = true;
                }
            }

            // Check history if not already found in current
            if (!wasActiveInMonth && g.history && Array.isArray(g.history)) {
                wasActiveInMonth = g.history.some((h: any) => {
                    const start = parseDate(h.postDate);
                    const end = parseDate(h.endDate) || parseDate(h.completedAt);
                    return start && start < nextMonthDate && (!end || end >= targetDate);
                });
            }

            // 2. Check if had any active attendance status recorded in this specific month
            const hasActivityInMonth = g.attendance && (
                (g.attendance.present || 0) > 0 ||
                (g.attendance.absent || 0) > 0 ||
                (g.attendance.leave || 0) > 0
            );

            // 3. Check for non-zero balance (dues)
            const hasBalance = g.attendance && (g.attendance.balance !== 0);

            return wasActiveInMonth || hasActivityInMonth || hasBalance;
        });

        // Apply search filter
        if (!searchTerm.trim()) {
            return result;
        }
        const term = searchTerm.toLowerCase();
        return result.filter((g) => {
            const name = g.name?.toLowerCase() || '';
            const guardId = g.guardId?.toLowerCase() || '';
            const cnic = g.cnic?.toLowerCase() || '';
            return name.includes(term) || guardId.includes(term) || cnic.includes(term);
        });
    }, [searchTerm, guards, selectedMonth, selectedYear]);

    // Pagination: one page of guards from Firebase; total pages from total count
    const totalPages = Math.max(1, Math.ceil(totalGuardCount / pageSize));
    const paginatedGuards = filteredGuards;

    // Generate page numbers with ellipsis
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            // Show all pages if total is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage > 3) {
                pages.push('ellipsis-start');
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                }
            }

            if (currentPage < totalPages - 2) {
                pages.push('ellipsis-end');
            }

            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }

        return pages;
    };

    // Calculate current day statistics
    const currentDayStats = useMemo(() => {
        let totalPresent = 0;
        let totalAbsent = 0;

        filteredGuards.forEach((guard) => {
            const status = guard.todayStatus;
            if (status === 'present') {
                totalPresent++;
            } else if (status === 'absent') {
                totalAbsent++;
            }
        });

        return { totalPresent, totalAbsent };
    }, [filteredGuards]);

    // Calculate statistics (total = all guards in company; current page count for averages)
    const totalGuards = totalGuardCount;
    const totalDaysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const averageAttendanceRate = filteredGuards.length > 0
        ? filteredGuards.reduce((sum, g) => {
            const daysToCount = (selectedYear === currentDate.year && selectedMonth === currentDate.month)
                ? currentDate.day
                : totalDaysInMonth;
            const rate = g.attendance
                ? (g.attendance.present / daysToCount) * 100
                : 0;
            return sum + rate;
        }, 0) / filteredGuards.length
        : 0;

    const handleMarkAttendance = (guard: GuardData) => {
        setSelectedGuard(guard);
        setShowAttendanceModal(true);
    };

    const handleViewHistory = (guard: GuardData) => {
        setHistoryGuard(guard);
        setShowHistoryModal(true);
    };

    const handleEditSalary = (guard: GuardData) => {
        setSalaryGuard(guard);
        setShowSalaryDeploymentModal(true);
    };

    const handleAttendanceUpdated = () => {
        // Reload data when attendance is updated and update Redux
        // Clear Redux cache to force fresh data load
        dispatch(clearAttendance());
        loadGuardsWithAttendance();
    };

    // Handle scrollbar auto-hide
    useEffect(() => {
        const tableBody = tableBodyRef.current;
        if (!tableBody) return;

        const handleScroll = () => {
            setShowScrollbar(true);
            tableBody.classList.add('scrolling');
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                setShowScrollbar(false);
                tableBody.classList.remove('scrolling');
            }, 2000);
        };

        tableBody.addEventListener('scroll', handleScroll);
        tableBody.addEventListener('mouseenter', () => {
            setShowScrollbar(true);
            tableBody.classList.add('scrolling');
        });
        tableBody.addEventListener('mouseleave', () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                setShowScrollbar(false);
                tableBody.classList.remove('scrolling');
            }, 2000);
        });

        return () => {
            tableBody.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);


    return (
        <DashboardLayout>
            <PageContainer>
                <SearchAndActionsContainer>
                    <SearchWrapper>
                        <Input
                            placeholder="Search by name, guard ID, or CNIC..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            dark
                        />
                    </SearchWrapper>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                            <Select
                                value={selectedMonth}
                                onChange={(e) => {
                                    setSelectedMonth(parseInt(e.target.value));
                                }}
                            >
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                    </option>
                                ))}
                            </Select>
                            <Select
                                value={selectedYear}
                                onChange={(e) => {
                                    setSelectedYear(parseInt(e.target.value));
                                }}
                            >
                                {Array.from({ length: 5 }).map((_, i) => {
                                    const year = currentDate.year - 2 + i;
                                    return <option key={year} value={year}>{year}</option>;
                                })}
                            </Select>
                        </div>
                        <Select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            title="Rows per page"
                            style={{ minWidth: 70 }}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </Select>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                dispatch(clearAttendance());
                                setCurrentPage(1);
                                pageCursorsRef.current = [];
                                loadGuardsWithAttendance();
                            }}
                            disabled={loading}
                            title="Refresh data"
                        >
                            <FiRotateCw size={18} style={{ marginRight: theme.spacing[1] }} />
                            Refresh
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowSalaryCycleConfig(true)}
                            title="Configure Salary Cycle"
                        >
                            <FiSettings size={18} style={{ marginRight: theme.spacing[1] }} />
                            Salary Cycle
                        </Button>
                        <div style={{ display: 'flex', gap: theme.spacing[2] }}>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowSalarySummary(true)}
                            >
                                Salary Summary
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowSalaryDistribution(true)}
                            >
                                Salary Distribution
                            </Button>
                        </div>
                    </div>
                </SearchAndActionsContainer>

                {/* Summary Cards */}
                <StatsContainer>
                    <StatCard>
                        <StatValue>{totalGuards}</StatValue>
                        <StatLabel>Total Guards</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue color={theme.colors.primary[600]}>{totalDaysInMonth}</StatValue>
                        <StatLabel>Total Days in Month</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue color={theme.colors.accent.info}>{currentDate.day}</StatValue>
                        <StatLabel>Days Elapsed</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue color={theme.colors.accent.success}>
                            {averageAttendanceRate.toFixed(1)}%
                        </StatValue>
                        <StatLabel>Average Attendance</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue color={theme.colors.accent.success}>
                            {currentDayStats.totalPresent}
                        </StatValue>
                        <StatLabel>Present Today</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue color={theme.colors.accent.danger}>
                            {currentDayStats.totalAbsent}
                        </StatValue>
                        <StatLabel>Absent Today</StatLabel>
                    </StatCard>
                </StatsContainer>

                {loading ? (
                    <LoadingContainer>
                        <Spinner />
                        <p style={{ color: theme.colors.light.text.secondary }}>Loading attendance...</p>
                    </LoadingContainer>
                ) : filteredGuards.length > 0 ? (
                    <>
                        <TableWrapper>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <th>Guard<br />Info</th>
                                        <th>Deployment<br />Info</th>
                                        <th>Present</th>
                                        <th>Absent</th>
                                        <th>Leave</th>
                                        <th>OT</th>
                                        <th>Monthly<br />Salary</th>
                                        <th>Salary Till<br />Today</th>
                                        <th>Advance<br />Salary</th>
                                        <th>Balance</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                            </StyledTable>
                            <TableBodyWrapper ref={tableBodyRef} showScrollbar={showScrollbar}>
                                <StyledTable>
                                    <tbody>
                                        {paginatedGuards.map((guard) => (
                                            <tr key={guard.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                                                        {guard.image ? (
                                                            <img
                                                                src={guard.image}
                                                                alt={guard.name}
                                                                style={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    objectFit: 'cover',
                                                                    borderRadius: '50%',
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: theme.colors.light.background.tertiary,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: theme.colors.light.text.muted,
                                                                }}
                                                            >
                                                                {guard.name?.charAt(0) || '?'}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div style={{ fontWeight: theme.typography.fontWeight.medium, color: theme.colors.light.text.primary }}>
                                                                {guard.name || '—'}
                                                            </div>
                                                            <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                                                                {guard.guardId || '—'} {guard.cnic ? `• ${guard.cnic}` : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                                                        {guard.currentDeployment?.postDate ? (
                                                            (() => {
                                                                const postDate = guard.currentDeployment.postDate as any;
                                                                let date: Date;
                                                                if (postDate.seconds) {
                                                                    date = new Date(postDate.seconds * 1000);
                                                                } else {
                                                                    date = new Date(postDate);
                                                                }
                                                                return (
                                                                    <div>
                                                                        <div style={{ fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600] }}>
                                                                            {guard.currentDeployment.client?.name || 'Deployed'}
                                                                            {(guard.currentDeployment.slotNumber || 0) > 100 && (
                                                                                <span style={{ color: theme.colors.accent.info, fontSize: '10px', marginLeft: '4px' }}>(OT)</span>
                                                                            )}
                                                                        </div>
                                                                        {date.toLocaleDateString()}
                                                                    </div>
                                                                );
                                                            })()
                                                        ) : (
                                                            // If not currently deployed, check history for the last deployment in this month
                                                            (() => {
                                                                if (guard.history && Array.isArray(guard.history)) {
                                                                    // Filter history for the selected month/year
                                                                    const monthHistory = guard.history.filter((h: any) => {
                                                                        const start = h.postDate?.seconds ? new Date(h.postDate.seconds * 1000) : new Date(h.postDate);
                                                                        const end = (h.endDate?.seconds ? new Date(h.endDate.seconds * 1000) : (h.endDate ? new Date(h.endDate) : (h.completedAt?.seconds ? new Date(h.completedAt.seconds * 1000) : (h.completedAt ? new Date(h.completedAt) : null))));

                                                                        if (!start) return false;

                                                                        const targetDate = new Date(selectedYear, selectedMonth - 1, 1);
                                                                        const nextMonthDate = new Date(selectedYear, selectedMonth, 1);

                                                                        // Deployment overlapped with the selected month
                                                                        return start < nextMonthDate && (!end || end >= targetDate);
                                                                    });

                                                                    if (monthHistory.length > 0) {
                                                                        // Show the most recent one for this month
                                                                        const lastH = monthHistory[0];
                                                                        return (
                                                                            <div>
                                                                                <div style={{ color: theme.colors.light.text.muted }}>
                                                                                    {lastH.client?.name || 'Past Deploy'}
                                                                                    {((lastH as any).slotNumber || 0) > 100 && (
                                                                                        <span style={{ color: theme.colors.accent.info, fontSize: '10px', marginLeft: '4px' }}>(OT)</span>
                                                                                    )}
                                                                                </div>
                                                                                <span style={{ fontSize: '10px' }}>(Undeployed)</span>
                                                                            </div>
                                                                        );
                                                                    }
                                                                }

                                                                // If no deployment in this month but has balance, show why they are here
                                                                if ((guard.attendance?.balance || 0) !== 0 && (guard.attendance?.present || 0) === 0) {
                                                                    return (
                                                                        <div style={{ color: theme.colors.accent.warning, fontSize: '11px', fontStyle: 'italic' }}>
                                                                            Outstanding Dues
                                                                        </div>
                                                                    );
                                                                }

                                                                return '—';
                                                            })()
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge variant="success">{guard.attendance?.present || 0}</Badge>
                                                </td>
                                                <td>
                                                    <Badge variant="danger">{guard.attendance?.absent || 0}</Badge>
                                                </td>
                                                <td>
                                                    <Badge variant="warning">{guard.attendance?.leave || 0}</Badge>
                                                </td>
                                                <td>
                                                    <Badge variant="info" style={{ backgroundColor: `${theme.colors.accent.info}20`, color: theme.colors.accent.info }}>
                                                        {guard.attendance?.ot || 0}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {guard.attendance?.monthlySalary
                                                        ? `Rs. ${guard.attendance.monthlySalary.toLocaleString()}`
                                                        : '—'}
                                                </td>
                                                <td>
                                                    <strong style={{ color: theme.colors.primary[600] }}>
                                                        {guard.attendance?.salaryTillToday
                                                            ? `Rs. ${guard.attendance.salaryTillToday.toLocaleString()}`
                                                            : '—'}
                                                    </strong>
                                                </td>
                                                <td>
                                                    {guard.attendance?.advanceSalary
                                                        ? `Rs. ${guard.attendance.advanceSalary.toLocaleString()}`
                                                        : 'Rs. 0'}
                                                </td>
                                                <td>
                                                    <strong style={{
                                                        color: (guard.attendance?.balance || 0) > 0
                                                            ? theme.colors.accent.danger
                                                            : theme.colors.accent.success
                                                    }}>
                                                        {guard.attendance?.balance !== undefined
                                                            ? `Rs. ${guard.attendance.balance.toLocaleString()}`
                                                            : 'Rs. 0'}
                                                    </strong>
                                                    {(guard.attendance?.openingBalance || 0) !== 0 && (
                                                        <div style={{ fontSize: '10px', color: theme.colors.light.text.secondary, marginTop: '2px' }}>
                                                            Includes Rs. {guard.attendance?.openingBalance?.toLocaleString()} dues
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'center' }}>
                                                        <ActionButton
                                                            onClick={() => handleMarkAttendance(guard)}
                                                            title="Mark Attendance"
                                                        >
                                                            <FiEye size={18} />
                                                        </ActionButton>
                                                        <ActionButton
                                                            onClick={() => handleViewHistory(guard)}
                                                            title="View History"
                                                        >
                                                            <FiClock size={18} />
                                                        </ActionButton>
                                                        <ActionButton
                                                            onClick={() => handleEditSalary(guard)}
                                                            title="Set Salary & Deployment Date"
                                                        >
                                                            <FiEdit size={18} />
                                                        </ActionButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </StyledTable>
                            </TableBodyWrapper>
                            <StyledTable>
                                <tfoot>
                                    <tr>
                                        <td colSpan={11}>
                                            <PaginationWrapper>
                                                <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                                                    <PaginationNavButton
                                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                        disabled={currentPage === 1 || loading}
                                                    >
                                                        <FaChevronLeft />
                                                    </PaginationNavButton>

                                                    {getPageNumbers().map((page, index) => {
                                                        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                                                            return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
                                                        }
                                                        const pageNum = page as number;
                                                        return (
                                                            <PageNumberButton
                                                                key={page}
                                                                active={currentPage === pageNum}
                                                                onClick={() => setCurrentPage(pageNum)}
                                                                disabled={loading || pageNum > currentPage + 1}
                                                                title={pageNum > currentPage + 1 ? 'Use Next to load more' : undefined}
                                                            >
                                                                {page}
                                                            </PageNumberButton>
                                                        );
                                                    })}

                                                    <PaginationNavButton
                                                        isNext
                                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                        disabled={currentPage >= totalPages || loading}
                                                    >
                                                        <FiChevronRight />
                                                    </PaginationNavButton>
                                                </div>
                                            </PaginationWrapper>
                                        </td>
                                    </tr>
                                </tfoot>
                            </StyledTable>
                        </TableWrapper>
                    </>
                ) : (
                    <EmptyState>
                        <EmptyStateIcon>📋</EmptyStateIcon>
                        <EmptyStateText>No guards found.</EmptyStateText>
                    </EmptyState>
                )}

                {selectedGuard && (
                    <AttendanceModal
                        guard={selectedGuard}
                        isOpen={showAttendanceModal}
                        onClose={() => {
                            setShowAttendanceModal(false);
                            setSelectedGuard(null);
                        }}
                        onUpdate={handleAttendanceUpdated}
                        companyId={companyId}
                        year={selectedYear}
                        month={selectedMonth}
                    />
                )}

                {historyGuard && (
                    <AttendanceHistoryModal
                        guard={historyGuard}
                        isOpen={showHistoryModal}
                        onClose={() => {
                            setShowHistoryModal(false);
                            setHistoryGuard(null);
                        }}
                        companyId={companyId}
                    />
                )}

                {showSalaryDistribution && (
                    <SalaryDistributionModal
                        isOpen={showSalaryDistribution}
                        onClose={() => setShowSalaryDistribution(false)}
                        guards={guards}
                        companyId={companyId}
                        onUpdate={handleAttendanceUpdated}
                    />
                )}

                {showSalarySummary && (
                    <SalarySummaryModal
                        isOpen={showSalarySummary}
                        onClose={() => setShowSalarySummary(false)}
                        companyId={companyId}
                    />
                )}

                {showSalaryCycleConfig && (
                    <SalaryCycleConfigModal
                        isOpen={showSalaryCycleConfig}
                        onClose={() => setShowSalaryCycleConfig(false)}
                        onSuccess={() => {
                            // Reload data after saving salary cycle config
                            dispatch(clearAttendance());
                            loadGuardsWithAttendance();
                        }}
                        companyId={companyId}
                    />
                )}

                <GuardSalaryDeploymentModal
                    guard={salaryGuard}
                    isOpen={showSalaryDeploymentModal}
                    onClose={() => {
                        setShowSalaryDeploymentModal(false);
                        setSalaryGuard(null);
                    }}
                    onUpdate={handleAttendanceUpdated}
                    companyId={companyId}
                />
            </PageContainer>
        </DashboardLayout>
    );
};

export default AttendancePage;
